import type { ReactNode } from "react";

import { GoogleSearch, SocialLinkedin, SWReactIcons, MicrosoftDynamics, Salesloft, Outlook } from "@similarweb/icons";
import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { DEFAULT_BOOLEAN_SETTINGS } from "app/pages/sales-intelligence/modules/extension-management/constants";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import type { ExtensionSettingsFieldKey, ExtensionSettings } from "../types/types";

import { getDefaultOverviewWidgets } from "./overviewWidgetsConfig";

const swIcon = (iconName: IconName): ReactNode => (
    <SWReactIcons
        iconName={iconName}
        size="xs"
        iconColor={colorsPalettes.carbon["500"]}
    />
);

export type AiWorkflowCardDef = {
    key: "enableGmailAgent" | "enableGoogleCalendarAgent";
    titleKey: string;
    descriptionKey: string;
    icon: ReactNode;
    isNew?: boolean;
};

export const AI_WORKFLOW_CARDS: AiWorkflowCardDef[] = [
    {
        key: "enableGmailAgent",
        titleKey: "si.management.extension.settings.gmail_agent.title",
        descriptionKey: "si.management.extension.settings.gmail_agent.description",
        icon: swIcon("craft-email"),
        isNew: true,
    },
    {
        key: "enableGoogleCalendarAgent",
        titleKey: "si.management.extension.settings.gcalendar_agent.title",
        descriptionKey: "si.management.extension.settings.gcalendar_agent.description",
        icon: swIcon("ai-calendar"),
        isNew: true,
    },
];

export const CRM_ENABLE_TO_AUTO_OPEN_KEY = {
    enableSalesforce: "autoOpenSalesforceEnabled",
    enableHubspot: "autoOpenHubspotEnabled",
    enableDynamics: "autoOpenDynamicsEnabled",
    enableSalesloft: "autoOpenSalesloftEnabled",
} as const;

export type CrmEnableKey = keyof typeof CRM_ENABLE_TO_AUTO_OPEN_KEY;
export type CrmAutoOpenKey = (typeof CRM_ENABLE_TO_AUTO_OPEN_KEY)[CrmEnableKey];

export const LINKEDIN_AUTO_OPEN_LABEL_KEY = "si.management.extension.settings.linkedin.auto_open";
export const CRM_AUTO_OPEN_LABEL_KEY = "si.management.extension.settings.crm.auto_open";

export type DataEnrichmentCardDef = {
    key: ExtensionSettingsFieldKey;
    titleKey: string;
    icon: ReactNode;
    autoOpenKey?: CrmAutoOpenKey;
};

export const DATA_ENRICHMENT_CRM_CARDS: DataEnrichmentCardDef[] = [
    {
        key: "enableSalesforce",
        titleKey: "si.management.extension.settings.salesforce.title",
        icon: swIcon("salesforce"),
        autoOpenKey: CRM_ENABLE_TO_AUTO_OPEN_KEY.enableSalesforce,
    },
    {
        key: "enableHubspot",
        titleKey: "si.management.extension.settings.hubspot.title",
        icon: swIcon("hubspot"),
        autoOpenKey: CRM_ENABLE_TO_AUTO_OPEN_KEY.enableHubspot,
    },
    {
        key: "enableDynamics",
        titleKey: "si.management.extension.settings.dynamics.title",
        icon: (
            <MicrosoftDynamics
                size="xs"
                color={colorsPalettes.carbon["500"]}
            />
        ),
        autoOpenKey: CRM_ENABLE_TO_AUTO_OPEN_KEY.enableDynamics,
    },
    {
        key: "enableSalesloft",
        titleKey: "si.management.extension.settings.salesloft.title",
        icon: (
            <Salesloft
                size="xs"
                color={colorsPalettes.carbon["500"]}
            />
        ),
        autoOpenKey: CRM_ENABLE_TO_AUTO_OPEN_KEY.enableSalesloft,
    },
    {
        key: "enableOutlookCalendar",
        titleKey: "si.management.extension.settings.outlook.title",
        icon: (
            <Outlook
                size="xs"
                color={colorsPalettes.carbon["500"]}
            />
        ),
    },
];

export const LINKEDIN_CARD: DataEnrichmentCardDef = {
    key: "enableLinkedin",
    titleKey: "si.management.extension.settings.linkedin.title",
    icon: (
        <SocialLinkedin
            size="xs"
            color={colorsPalettes.carbon["500"]}
        />
    ),
};

export const GOOGLE_WORKSPACE_CARD: DataEnrichmentCardDef = {
    key: "enableGoogleWorkspace",
    titleKey: "si.management.extension.settings.google_workspace.title",
    icon: (
        <GoogleSearch
            size="xs"
            color={colorsPalettes.carbon["500"]}
        />
    ),
};

export type GoogleWorkspaceChildDef = {
    key: "enableGmail" | "enableGoogleCalendar" | "enableGoogleSheets";
    titleKey: string;
};

export const GOOGLE_WORKSPACE_CHILDREN: GoogleWorkspaceChildDef[] = [
    { key: "enableGmail", titleKey: "si.management.extension.settings.gmail_enrichment.title" },
    { key: "enableGoogleCalendar", titleKey: "si.management.extension.settings.gcalendar_enrichment.title" },
    { key: "enableGoogleSheets", titleKey: "si.management.extension.settings.google_sheets.title" },
];

export const FLOATING_ICON_ROW = {
    key: "showFloatingBadge" as const,
    titleKey: "si.management.extension.settings.floating_icon.title",
    descriptionKey: "si.management.extension.settings.floating_icon.description",
};

export const getDefaultExtensionSettings = (): ExtensionSettings => {
    const industry = salesSettingsHelper.getFormattedCompanyIndustry();
    const widgets = getDefaultOverviewWidgets(industry);
    return {
        ...DEFAULT_BOOLEAN_SETTINGS,
        overviewWidgets: widgets,
    };
};
